import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */

/**
 * Prefetches /checkout into the browser HTTP cache. When the user clicks
 * Checkout in the cart drawer, most of the HTML is already fetched. No pixel
 * side effects: prefetch is a passive cache warm-up, not a navigation.
 * Module-level so both ProductFormComponent and PetVariantFlow can call it.
 */
function prefetchCheckoutOnce() {
  const checkoutUrl =
    (typeof Theme !== 'undefined' && Theme.routes?.checkout_url) || '/checkout';
  if (document.querySelector(`link[rel="prefetch"][href="${checkoutUrl}"]`)) return;
  const prefetch = document.createElement('link');
  prefetch.rel = 'prefetch';
  prefetch.href = checkoutUrl;
  prefetch.as = 'document';
  document.head.appendChild(prefetch);

  const origin = window.location.origin;
  if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = origin;
    document.head.appendChild(preconnect);
  }
}

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {AbortController | undefined} */
  #petCountListenerAbort;

  /** @type {AbortController | undefined} */
  #petSwitchAbortController;

  /** @type {Record<string, {productId: number, productUrl: string, featuredImage: string, variantMap: Record<string, number>}> | null} */
  #petProductsMap = null;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#initPetCountSync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initPetCountSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#petCountListenerAbort?.abort();
    this.#petSwitchAbortController?.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    this.#runStandardSubmit(event);
  }

  /**
   * @param {Event} event - The submit event.
   */
  #runStandardSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    // Include cart section IDs so the server-rendered cart drawer HTML
    // comes back with the /cart/add.js response and can be morphed in.
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    // CRO 2026-04-20: if the form includes a File (custom-pet uploads), use
    // XHR so we can hook `upload.onprogress` and drive a real progress bar.
    // For standard products (no files), stick with fetch — no visible win.
    let hasFile = false;
    for (const value of formData.values()) {
      if (value instanceof File && value.size > 0) {
        hasFile = true;
        break;
      }
    }

    const responsePromise = hasFile
      ? this.#postWithUploadProgress(Theme.routes.cart_add_url, formData, fetchCfg)
      : fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        }).then((r) => r.json());

    responsePromise
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fire the CartAddEvent *first* so the cart drawer re-renders with
          // the new item and slides open without waiting on the cart-qty
          // refetch below. This is the user-perceived latency win — everything
          // that changes the UI happens in the same microtask as the response.
          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // Belt-and-suspenders: explicitly open the cart drawer. The
          // CartAddEvent listener on cart-drawer-component already does this
          // when `auto-open` is set, but calling .open() directly guarantees
          // the drawer slides in even if a listener swallows the event or the
          // drawer element is re-morphed before its handler can run.
          const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (
            document.querySelector('cart-drawer-component')
          );
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          }

          // Fire-and-forget: refresh the variant quantity UI (inline "(N in
          // cart)" label, volume pricing, quantity rules) only if this PDP
          // actually shows any of those refs. Doesn't block the drawer.
          const shouldUpdateCartQty =
            this.refs.quantityLabelCartCount ||
            this.refs.quantityRules ||
            this.refs.pricePerItem ||
            this.refs.volumePricing ||
            this.refs.quantitySelector;

          if (shouldUpdateCartQty) {
            this.#fetchAndUpdateCartQuantity().catch((err) => console.error(err));
          }

          // Prefetch /checkout once the item is in the cart. By the time the
          // user clicks Checkout in the drawer, most of the page HTML is
          // already cached. No pixel side-effects (prefetch != navigation).
          this.#prefetchCheckout();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * POSTs multipart FormData via XHR so `upload.onprogress` can drive a real
   * progress bar (CRO 2026-04-20 — tier-2 upload feedback for custom-pet
   * products where the File payload is large enough to matter). Dispatches
   * a bubbling `pet-flow:upload-progress` event on the document during the
   * upload and a `pet-flow:upload-complete` event when bytes are all on the
   * wire (server is still processing). Resolves with the parsed JSON body.
   *
   * Kept in product-form.js so it ships in the same bundle as the caller and
   * shares the same fetchConfig headers (cookies, CSRF, Accept-Language).
   *
   * @param {string} url
   * @param {FormData} formData
   * @param {RequestInit} baseCfg - used only to copy `credentials` and headers
   * @returns {Promise<any>}
   */
  #postWithUploadProgress(url, formData, baseCfg) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);

      // Mirror fetchConfig headers — but DO NOT set Content-Type. The browser
      // must set it with the correct multipart boundary automatically.
      const headers = { ...(baseCfg?.headers || {}), Accept: 'text/html' };
      for (const [k, v] of Object.entries(headers)) {
        if (k.toLowerCase() === 'content-type') continue;
        if (typeof v === 'string') xhr.setRequestHeader(k, v);
      }
      xhr.withCredentials = true;

      // Real upload progress — covers bytes going over the wire.
      xhr.upload.addEventListener('progress', (e) => {
        if (!e.lengthComputable) return;
        const percent = Math.max(0, Math.min(100, (e.loaded / e.total) * 100));
        document.dispatchEvent(
          new CustomEvent('pet-flow:upload-progress', {
            detail: { loaded: e.loaded, total: e.total, percent },
            bubbles: true,
          })
        );
      });
      xhr.upload.addEventListener('load', () => {
        // Bytes are uploaded — server is now generating the response. The
        // tween still clamps at 98% asymptote until CartAddEvent fires, so
        // sending 100 here just raises the tween's real-progress floor to
        // its ceiling and the bar holds ~98% during server-side processing.
        document.dispatchEvent(
          new CustomEvent('pet-flow:upload-complete', {
            detail: { percent: 100 },
            bubbles: true,
          })
        );
      });

      xhr.addEventListener('load', () => {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (err) {
          reject(err);
        }
      });
      xhr.addEventListener('error', () => reject(new Error('Network error during /cart/add.js')));
      xhr.addEventListener('abort', () => reject(new Error('/cart/add.js aborted')));

      xhr.send(formData);
    });
  }

  /**
   * Returns the pet flow "Next/Checkout" button for this product, if present.
   * @returns {HTMLButtonElement | null}
   */
  /**
   * Prefetches /checkout into the browser HTTP cache after a successful
   * add-to-cart. When the user clicks Checkout in the cart drawer, most
   * of the checkout HTML is already fetched. No pixel side effects:
   * prefetch is a passive cache warm-up, not a navigation.
   */
  #prefetchCheckout() {
    prefetchCheckoutOnce();
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   * Skipped when optimistic client-side data was already applied (button state is already correct).
   * @param {Event} event
   */
  #onVariantSelected = (event) => {
    if (event.detail?.resource?.optimistic) return;
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Syncs custom pet count selector with the product form line item property.
   */
  #initPetCountSync() {
    // Abort any previous listeners from a prior call (critical after morph re-binds)
    this.#petCountListenerAbort?.abort();
    this.#petCountListenerAbort = new AbortController();
    const { signal } = this.#petCountListenerAbort;

    const productId = this.dataset.productId;
    if (!productId) return;

    const propertyInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[data-pet-count-property]')
    );
    if (!propertyInput) return;

    // Query pet selector — try current productId first, fall back to any pet selector
    let selectorRoot = /** @type {HTMLElement | null} */ (
      document.querySelector(`.custom-pet__options[data-product-id="${productId}"]`)
    );
    if (!selectorRoot) {
      selectorRoot = /** @type {HTMLElement | null} */ (
        document.querySelector('.custom-pet__options')
      );
    }
    if (!selectorRoot) return;

    const petInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      selectorRoot.querySelectorAll('input[data-pet-count-value]')
    );
    if (!petInputs.length) return;

    const storageKey = 'pupsoul_pet_count';

    const updatePropertyFromSelection = () => {
      const checked = Array.from(petInputs).find((input) => input.checked);
      propertyInput.value = checked?.dataset.petCountValue || '';
    };

    // Restore previous selection from sessionStorage
    let storedValue;
    if (window.sessionStorage) {
      try {
        storedValue = window.sessionStorage.getItem(storageKey);
      } catch (e) {
        storedValue = null;
      }
    }

    if (storedValue) {
      let matched = false;
      petInputs.forEach((input) => {
        if (input.dataset.petCountValue === storedValue) {
          input.checked = true;
          matched = true;
        } else {
          input.checked = false;
        }
      });
      updatePropertyFromSelection();

      // If restored selection is for a different product, swap locally on load
      if (matched) {
        const petCount = storedValue.replace('pet', '');
        const map = this.#loadPetProductsMap();
        const targetData = map?.[petCount];
        if (targetData && String(targetData.productId) !== productId) {
          this.#switchPetProductLocal(petCount);
        }
      }
    } else {
      updatePropertyFromSelection();
    }

    petInputs.forEach((input) => {
      input.addEventListener('change', () => {
        updatePropertyFromSelection();

        // Persist selection
        if (window.sessionStorage) {
          try {
            window.sessionStorage.setItem(storageKey, input.dataset.petCountValue || '');
          } catch (e) { /* ignore */ }
        }

        // Determine if product switch is needed
        const petValue = input.dataset.petCountValue || '';
        const petCount = petValue.replace('pet', '');
        const currentProductId = this.dataset.productId;
        const map = this.#loadPetProductsMap();
        const targetData = map?.[petCount];

        if (targetData && String(targetData.productId) !== currentProductId) {
          // Try instant local swap first (no fetch, no morph, no reload)
          if (!this.#switchPetProductLocal(petCount)) {
            // Fallback: fetch + morph (old approach)
            const targetUrl = input.dataset.productUrl;
            if (targetUrl && !window.location.pathname.includes(new URL(targetUrl, window.location.origin).pathname)) {
              this.#switchPetProduct(targetUrl);
            }
          }
        }
      }, { signal });
    });
  }

  /**
   * Switches to a different pet-count product via AJAX morph instead of hard navigation.
   * Falls back to window.location.href on error so the user is never stranded.
   * @param {string} targetUrl
   */
  async #switchPetProduct(targetUrl) {
    this.#petSwitchAbortController?.abort();
    this.#petSwitchAbortController = new AbortController();

    this.dataset.switchingProduct = 'true';

    try {
      const response = await fetch(targetUrl, { signal: this.#petSwitchAbortController.signal });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const currentMain = document.querySelector('main');
      const newMain = html.querySelector('main');

      if (!currentMain || !newMain) {
        throw new Error('Could not find main element for morph');
      }

      // Before the morph replaces the gallery, snapshot the current slides 2..N
      // and thumbnails 2..N so we can restore the original trust-building media
      // after the morph. Slot #1 takes the new product's variant image.
      const oldGallery = currentMain.querySelector('media-gallery');
      const preservedSlides = oldGallery
        ? Array.from(oldGallery.querySelectorAll('slideshow-slide')).slice(1).map((n) => n.cloneNode(true))
        : [];
      const preservedThumbs = oldGallery
        ? Array.from(oldGallery.querySelectorAll('.slideshow-controls__thumbnail')).slice(1).map((n) => n.cloneNode(true))
        : [];

      morph(currentMain, newMain);

      // Restore preserved slides/thumbs 2..N into the freshly morphed gallery.
      const morphedGallery = document.querySelector('main media-gallery');
      if (morphedGallery && preservedSlides.length) {
        const slides = morphedGallery.querySelectorAll('slideshow-slide');
        const firstSlide = slides[0];
        slides.forEach((slide, i) => { if (i > 0) slide.remove(); });
        if (firstSlide?.parentNode) {
          preservedSlides.forEach((s) => firstSlide.parentNode.appendChild(s));
        }
      }
      if (morphedGallery && preservedThumbs.length) {
        const thumbs = morphedGallery.querySelectorAll('.slideshow-controls__thumbnail');
        const firstThumb = thumbs[0];
        thumbs.forEach((thumb, i) => { if (i > 0) thumb.remove(); });
        if (firstThumb?.parentNode) {
          preservedThumbs.forEach((t) => firstThumb.parentNode.appendChild(t));
        }
      }

      // Use setTimeout instead of yieldToMainThread — the latter's promise chain
      // silently fails to execute replaceState in this context (confirmed via testing).
      setTimeout(() => {
        history.replaceState({}, '', targetUrl);
      }, 50);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fallback to hard navigation so the user is never stranded
      window.location.href = targetUrl;
    } finally {
      delete this.dataset.switchingProduct;
    }
  }

  /**
   * Lazily loads and caches the pet products map from the inline JSON script tag.
   * @returns {Record<string, {productId: number, productUrl: string, featuredImage: string, variantMap: Record<string, number>}> | null}
   */
  #loadPetProductsMap() {
    if (this.#petProductsMap) return this.#petProductsMap;
    const script = document.querySelector('script[data-pet-products-map]');
    if (!script) return null;
    try {
      this.#petProductsMap = JSON.parse(script.textContent || '');
    } catch (e) {
      console.error('Failed to parse pet products map:', e);
      return null;
    }
    return this.#petProductsMap;
  }

  /**
   * Switches to a different pet-count product by swapping data locally — no fetch.
   * Updates variant IDs, product references, and gallery image instantly.
   * Falls back to false if the map is unavailable so caller can try the fetch path.
   * @param {string} petCount — "1", "2", "3", or "4"
   * @returns {boolean} — true if swap succeeded
   */
  #switchPetProductLocal(petCount) {
    const map = this.#loadPetProductsMap();
    if (!map || !map[petCount]) return false;

    const productData = map[petCount];
    const section = this.closest('.shopify-section');
    if (!section) return false;

    const newProductId = String(productData.productId);
    const newProductUrl = productData.productUrl;

    // --- 1. Read current option selections from the variant picker ---
    const variantPicker = section.querySelector('variant-picker');
    const optionGroups = variantPicker
      ? Array.from(variantPicker.querySelectorAll('.variant-picker__form .variant-option'))
      : [];
    const currentSelections = optionGroups.map((group) => {
      const checked = group.querySelector('input:checked');
      if (checked instanceof HTMLInputElement) return checked.value;
      const select = group.querySelector('select');
      if (select instanceof HTMLSelectElement) return select.value;
      return '';
    });

    // --- 2. Look up variant ID for current option combo ---
    const variantKey = currentSelections.join('|');
    let variantId = productData.variantMap[variantKey];
    if (!variantId) {
      // Fallback: first available variant
      const firstKey = Object.keys(productData.variantMap)[0];
      variantId = firstKey ? productData.variantMap[firstKey] : null;
    }
    if (!variantId) return false;

    // --- 3. Update hidden variant ID input ---
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = String(variantId);
    }
    const productIdInput = this.querySelector('input[name="product-id"]');
    if (productIdInput instanceof HTMLInputElement) {
      productIdInput.value = newProductId;
    }

    // --- 4. Update data-variant-id and data-option-value-id on every option
    //        input so subsequent variant-picker fetches hit the correct product ---
    const optionValueIds = productData.optionValueIds || {};

    optionGroups.forEach((group, optionIndex) => {
      // Determine this option group's name from the legend text (e.g. "Background Color:White")
      const legendText = group.querySelector('legend')?.textContent?.trim() || '';
      const optionName = legendText.split(':')[0].trim();
      const optionIdMap = optionValueIds[optionName] || {};

      // Radio/checkbox inputs
      group.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const testSelections = [...currentSelections];
        testSelections[optionIndex] = input.value;
        const testKey = testSelections.join('|');
        const testVariantId = productData.variantMap[testKey];
        if (testVariantId) {
          input.dataset.variantId = String(testVariantId);
        }
        // Update option value ID for section fetch requests
        const newOptionValueId = optionIdMap[input.value];
        if (newOptionValueId) {
          input.dataset.optionValueId = String(newOptionValueId);
        }
      });
      // Select options (dropdown mode)
      group.querySelectorAll('select option').forEach((option) => {
        if (!(option instanceof HTMLOptionElement)) return;
        const testSelections = [...currentSelections];
        testSelections[optionIndex] = option.value;
        const testKey = testSelections.join('|');
        const testVariantId = productData.variantMap[testKey];
        if (testVariantId) {
          option.dataset.variantId = String(testVariantId);
        }
        const newOptionValueId = optionIdMap[option.value];
        if (newOptionValueId) {
          option.dataset.optionValueId = String(newOptionValueId);
        }
      });
    });

    // --- 5. Update product ID and URL on all relevant elements ---
    this.dataset.productId = newProductId;
    this.dataset.productUrl = newProductUrl;

    if (variantPicker) {
      variantPicker.dataset.productId = newProductId;
      variantPicker.dataset.productUrl = newProductUrl;
    }

    const petFlow = section.querySelector('pet-variant-flow');
    if (petFlow) petFlow.dataset.productId = newProductId;

    const petDetails = section.querySelector('[data-pet-details-container]');
    if (petDetails) petDetails.dataset.productId = newProductId;

    const petNav = section.querySelector('[data-pet-flow-nav]');
    if (petNav) petNav.dataset.productId = newProductId;

    const indicator = document.querySelector('[data-pet-flow-indicator]');
    if (indicator) indicator.dataset.productId = newProductId;

    const petSelector = document.querySelector('.custom-pet__options');
    if (petSelector) petSelector.dataset.productId = newProductId;

    // --- 6. Update gallery image ---
    // Prefer the SELECTED VARIANT'S image over the product's default
    // featured_image. N-pet products often have Black set as the admin-level
    // default variant — using productData.featuredImage there produced a Black
    // frame preview even when the user still had White selected. Look up the
    // current variant's image from the variant-data JSON blob; fall back to
    // the product featured image only if the lookup fails.
    let galleryImageUrl = productData.featuredImage;
    try {
      const variantDataScript = section.querySelector('script[data-variant-data]');
      if (variantDataScript instanceof HTMLScriptElement) {
        const variantData = JSON.parse(variantDataScript.textContent || '{}');
        const petBucket = variantData[petCount];
        const variantInfo = petBucket ? petBucket[String(variantId)] : null;
        if (variantInfo && variantInfo.image) {
          galleryImageUrl = variantInfo.image;
        }
      }
    } catch {
      /* fall through to product featured image */
    }
    this.#updateGalleryImage(section, galleryImageUrl);

    // --- 6b. Swap size-swatch thumbnails between vertical and horizontal
    //          orientations. Multi-pet products only ship in horizontal
    //          layouts, so their size selector must show H frames. ---
    this.#updateSizeSwatchOrientation(section, petCount);

    // --- 7. Optimistic price update for the new product's variant ---
    this.#updatePriceForVariant(section, petCount, String(variantId));

    return true;
  }

  /**
   * Swaps size-selector thumbnails between vertical (single-pet products)
   * and horizontal (multi-pet products) orientations. The server renders
   * both URLs into data-image-v / data-image-h attributes on the <img>;
   * this picks one based on pet count.
   * @param {Element} section
   * @param {string} petCount — "1", "2", "3", or "4"
   */
  #updateSizeSwatchOrientation(section, petCount) {
    const useHorizontal = petCount !== '1';
    const swatchImages = section.querySelectorAll('img[data-size-swatch-image]');
    swatchImages.forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      const targetUrl = useHorizontal ? img.dataset.imageH : img.dataset.imageV;
      if (targetUrl && img.src !== targetUrl) {
        img.src = targetUrl;
        img.removeAttribute('srcset');
      }
    });
  }

  /**
   * Swaps the product gallery image for an instant pet-count switch.
   * @param {Element} section — The .shopify-section container
   * @param {string} imageUrl — The new image URL
   */
  #updateGalleryImage(section, imageUrl) {
    if (!imageUrl) return;
    const gallery = section.querySelector('media-gallery');
    if (!gallery) return;

    // Scope the preview swap to slot #1 so slides 2..N keep the
    // original trust-building product media the shopper can still swipe to.
    const firstSlide = gallery.querySelector('slideshow-slide');
    if (firstSlide) {
      firstSlide.querySelectorAll('img').forEach((img) => {
        if (img instanceof HTMLImageElement) {
          img.src = imageUrl;
          img.removeAttribute('srcset');
        }
      });
    }

    const firstThumbImg = gallery.querySelector('.slideshow-controls__thumbnail img');
    if (firstThumbImg instanceof HTMLImageElement) {
      firstThumbImg.src = imageUrl;
      firstThumbImg.removeAttribute('srcset');
    }

    // Snap to slot #1 so a shopper who scrolled to a later slide sees the change.
    const firstThumbBtn = gallery.querySelector('.slideshow-controls__thumbnail');
    if (firstThumbBtn instanceof HTMLElement) firstThumbBtn.click();

    // Also update the mobile inline preview
    const petFlow = section.querySelector('pet-variant-flow');
    if (petFlow && typeof petFlow.updateMobilePreview === 'function') {
      petFlow.updateMobilePreview(imageUrl);
    }
  }

  /**
   * Updates price display for a given variant using the client-side variant data cache.
   * Used during pet-count switching to show the correct price instantly.
   * @param {Element} section — The .shopify-section container
   * @param {string} petCount — Pet count key ("1", "2", "3", "4")
   * @param {string} variantId — The variant ID to look up
   */
  #updatePriceForVariant(section, petCount, variantId) {
    const script = section.querySelector('script[data-variant-data]');
    if (!script) return;

    let allData;
    try {
      allData = JSON.parse(script.textContent || '{}');
    } catch {
      return;
    }

    const variants = allData[petCount];
    if (!variants || !variants[variantId]) return;

    const v = variants[variantId];
    const format = (typeof Theme !== 'undefined' && Theme.moneyFormat) || '${{amount}}';

    const priceEl = section.querySelector('product-price .price');
    const compareEl = section.querySelector('product-price .compare-at-price');

    if (priceEl) {
      priceEl.textContent = this.#formatMoney(v.price, format);
    }

    if (compareEl) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        compareEl.textContent = this.#formatMoney(v.compare_at_price, format);
        compareEl.closest('[role="group"]')?.removeAttribute('hidden');
      } else {
        compareEl.textContent = '';
        const group = compareEl.closest('[role="group"]');
        if (group) group.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Formats a price in cents using Shopify's money format string.
   * @param {number} cents
   * @param {string} format
   * @returns {string}
   */
  #formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const [whole, fraction] = amount.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const withPeriods = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const withApostrophes = whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    return format
      .replace('{{amount}}', withCommas + '.' + fraction)
      .replace('{{amount_no_decimals}}', withCommas)
      .replace('{{amount_with_comma_separator}}', withPeriods + ',' + fraction)
      .replace('{{amount_no_decimals_with_comma_separator}}', withPeriods)
      .replace('{{amount_with_apostrophe_separator}}', withApostrophes + '.' + fraction);
  }
}

class PetVariantFlow extends HTMLElement {
  /** @type {HTMLElement[] | undefined} */
  #optionGroups;
  /** @type {HTMLButtonElement | undefined} */
  #nextButton;
  /** @type {AbortController | undefined} — controls variant-selected URL-sync listener */
  #urlSyncAbort;
  /** @type {HTMLButtonElement | undefined} */
  #previousButton;
  /** @type {NodeListOf<HTMLInputElement> | undefined} */
  #petInputs;
  /** @type {NodeListOf<HTMLInputElement> | undefined} */
  #imageInputs;
  /** @type {NodeListOf<HTMLInputElement> | undefined} */
  #nameInputs;
  /** @type {number} */
  #currentMaxIndex = -1;
  /** @type {HTMLElement | undefined} */
  #petSelectorRoot;
  /** @type {Element | null | undefined} */
  #variantUpdateTarget;
  /** @type {(event: any) => void} */
  #boundVariantUpdateHandler;
  /** @type {((event: Event) => void) | null} */
  #boundClearLoading = null;
  /** @type {((event: Event) => void) | null} */
  #boundUploadProgress = null;
  /** @type {((event: Event) => void) | null} */
  #boundUploadComplete = null;
  /** @type {((event: Event) => void) | null} */
  #boundShowCartError = null;
  /** @type {string} */
  #nextButtonDefaultLabel = 'Next';
  /** @type {HTMLElement | null} */
  #errorEl = null;
  /** @type {HTMLElement | null} */
  #errorTextEl = null;
  /** @type {number | null} */
  #errorHideTimeoutId = null;
  /** @type {AbortController | null} — scoped controller for drag-and-drop dropzone listeners */
  #dropzoneAbort = null;
  /** @type {HTMLElement | null} */
  #stickyCta = null;
  /** @type {HTMLButtonElement | null} */
  #stickyCtaButton = null;
  /** @type {HTMLElement | null} */
  #stickyCtaPriceEl = null;
  /** @type {HTMLElement | null} */
  #stickyCtaButtonTextEl = null;
  /** @type {IntersectionObserver | null} */
  #stickyCtaObserver = null;
  /** @type {((event: Event) => void) | null} */
  #boundStickyPriceSync = null;
  /** @type {boolean} — run once per element lifetime */
  #initialWhiteWhitePinned = false;
  /** @type {HTMLElement | null} */
  #indicatorRoot = null;
  /** @type {HTMLElement | null} */
  #indicatorLabel = null;
  /** @type {HTMLElement | null} */
  #indicatorSummary = null;
  /** @type {HTMLElement[]} */
  #indicatorDots = [];
  /** @type {HTMLElement[]} */
  #indicatorConnectors = [];
  /** @type {HTMLElement | null} — mobile-only inline preview container */
  #mobilePreview = null;
  /** @type {HTMLImageElement | null} — mobile preview <img> */
  #mobilePreviewImg = null;

  /** Human-readable labels for each step (populated from data attributes) */
  #stepLabels = {
    '-1': 'How many pets in your portrait?',
    details: 'Upload photos & name your pets',
  };

  connectedCallback() {
    this.#collectOptionGroups();
    this.#hideAllOptionGroups();

    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');

    // --- Step Indicator ---
    this.#initStepIndicator(productId);

    // --- Mobile Inline Preview ---
    this.#initMobilePreview();

    // --- Nav Buttons ---
    const nav = productId
      ? section?.querySelector(`[data-pet-flow-nav][data-product-id="${productId}"]`)
      : null;

    const nextButton = nav?.querySelector('[data-pet-flow-next]');
    if (nextButton instanceof HTMLButtonElement) {
      this.#nextButton = nextButton;
      const textSpan = this.#nextButton.querySelector('.pet-flow__next-button-text');
      if (textSpan instanceof HTMLElement && textSpan.textContent?.trim()) {
        this.#nextButtonDefaultLabel = textSpan.textContent.trim();
      }
      // Inject spinner + success-check slots if not already in the DOM. Done
      // in JS (rather than Liquid) so the markup stays optional — the button
      // still works if a future theme update removes these elements.
      if (!this.#nextButton.querySelector('.pet-flow__next-button-spinner')) {
        const spinner = document.createElement('span');
        spinner.className = 'pet-flow__next-button-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        this.#nextButton.insertBefore(spinner, this.#nextButton.firstChild);
      }
      if (!this.#nextButton.querySelector('.pet-flow__next-button-check')) {
        const check = document.createElement('span');
        check.className = 'pet-flow__next-button-check';
        check.setAttribute('aria-hidden', 'true');
        check.innerHTML = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 9.5l3.2 3.2L14 6" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        this.#nextButton.insertBefore(check, this.#nextButton.firstChild);
      }
      this.#nextButton.addEventListener('click', this.#handleNextClick);
    }

    // Locate the error element rendered by buy-buttons.liquid. If it exists,
    // CartErrorEvent payloads will be rendered into it so the user never
    // sees a silent ATC failure (see #boundShowCartError below).
    if (productId) {
      this.#errorEl = /** @type {HTMLElement | null} */ (
        section?.querySelector(`[data-pet-flow-error][data-product-id="${productId}"]`)
      );
      this.#errorTextEl = this.#errorEl?.querySelector('[data-pet-flow-error-text]') ?? null;
    }

    const previousButton = nav?.querySelector('[data-pet-flow-prev]');
    if (previousButton instanceof HTMLButtonElement) {
      this.#previousButton = previousButton;
      this.#previousButton.addEventListener('click', this.#handlePrevClick);
    }

    // --- Pet count selector ---
    if (productId) {
      const selectorRoot = /** @type {HTMLElement | null} */ (
        document.querySelector(`.custom-pet__options[data-product-id="${productId}"]`)
      );
      if (selectorRoot) {
        this.#petSelectorRoot = selectorRoot;
        this.#petInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
          selectorRoot.querySelectorAll('input[data-pet-count-value]')
        );
        this.#petInputs.forEach((input) => {
          input.addEventListener('change', this.#handlePetChange);
        });
      }

      const detailsContainer = section?.querySelector(
        `[data-pet-details-container][data-product-id="${productId}"]`
      );
      if (detailsContainer) {
        this.#imageInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
          detailsContainer.querySelectorAll('input[data-pet-details-image]')
        );
        this.#imageInputs.forEach((input) => {
          input.addEventListener('change', this.#handleImageChange);
        });

        // Drag-and-drop: the locale copy promises "Drag and drop your pet's
        // photo or Browse" — actually wire it up. Each .pet-image-upload__label
        // becomes a dropzone. On drop we hand the File to the underlying input
        // via DataTransfer and dispatch a synthetic change event so the
        // existing #handleImageChange pipeline (filename display, compression,
        // next-button enable) runs unchanged.
        this.#dropzoneAbort = new AbortController();
        const dropSignal = this.#dropzoneAbort.signal;
        detailsContainer
          .querySelectorAll('.pet-image-upload__label')
          .forEach((label) => {
            if (!(label instanceof HTMLLabelElement)) return;
            const input = /** @type {HTMLInputElement | null} */ (
              label.querySelector('input[data-pet-details-image]')
            );
            if (!input) return;

            let depth = 0; // handles dragenter firing on child elements

            label.addEventListener(
              'dragenter',
              (event) => {
                event.preventDefault();
                depth += 1;
                label.classList.add('is-dragover');
              },
              { signal: dropSignal }
            );

            label.addEventListener(
              'dragover',
              (event) => {
                event.preventDefault();
                if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
              },
              { signal: dropSignal }
            );

            label.addEventListener(
              'dragleave',
              () => {
                depth = Math.max(0, depth - 1);
                if (depth === 0) label.classList.remove('is-dragover');
              },
              { signal: dropSignal }
            );

            label.addEventListener(
              'drop',
              (event) => {
                event.preventDefault();
                depth = 0;
                label.classList.remove('is-dragover');

                const files = event.dataTransfer?.files;
                if (!files || files.length === 0) return;

                // Accept first image file only. Silently ignore others so the
                // user doesn't see a scary "wrong file type" flash for e.g.
                // a dragged PDF — the input stays empty and the step remains
                // blocked, which is the same outcome as not dropping anything.
                const file = Array.from(files).find((f) => f.type.startsWith('image/'));
                if (!file) return;

                const dt = new DataTransfer();
                dt.items.add(file);
                input.files = dt.files;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              },
              { signal: dropSignal }
            );
          });

        this.#nameInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
          detailsContainer.querySelectorAll('input[data-pet-details-name]')
        );
        this.#nameInputs.forEach((input) => {
          input.addEventListener('input', this.#handleNameChange);
        });
      }
    }

    this.#syncUiFromState();

    const target = this.closest('.shopify-section, dialog, product-card');
    this.#variantUpdateTarget = target;
    this.#boundVariantUpdateHandler = this.#handleVariantUpdate.bind(this);
    target?.addEventListener(ThemeEvents.variantUpdate, this.#boundVariantUpdateHandler);

    // Silent-add listeners removed 2026-04-20 — ATC now fires once on user
    // submit only. Pixel data is clean and the cart drawer opens instead.

    // Clear the pet-flow "Adding…" loading state once /cart/add.js resolves.
    // CartAddEvent fires on success, CartErrorEvent on failure; we hook both
    // on document (events bubble from product-form-component) so the user
    // never sees a stuck "Adding…" label. On success we pass `true` for
    // showSuccessBeat so the button briefly shows a ✓ "Added!" state before
    // the cart drawer opens — gives completion feedback, feels intentional.
    this.#boundClearLoading = (event) => {
      const succeeded = event?.type === CartAddEvent.eventName
        && !(/** @type {any} */ (event).detail?.didError);
      this.#setNextButtonLoading(false, { succeeded });
    };
    document.addEventListener(CartAddEvent.eventName, this.#boundClearLoading);
    document.addEventListener(CartErrorEvent.eventName, this.#boundClearLoading);

    // Render a visible error pill under the pet-flow controls whenever
    // /cart/add.js returns a rejection (sold-out, validation, rate limit).
    // Critical fix (2026-04-21): without this, the pet-flow submit path
    // would silently swallow server errors — users clicked "Add to Cart",
    // saw nothing, and bounced. This is the revenue leak from this week.
    // Note: CartErrorEvent extends `Event` (not CustomEvent) but still
    // carries a `.detail` property set in its constructor. Treat any event
    // with a detail shape as valid.
    this.#boundShowCartError = (event) => {
      const detail = /** @type {any} */ (event)?.detail || {};
      // The CartErrorEvent constructor places the user-facing text at
      // detail.data.message; some call sites may instead use detail.message.
      const message =
        detail.data?.message
        || detail.message
        || detail.data?.description
        || 'Something went wrong adding this to your cart. Please try again.';
      this.#showPetFlowError(message);
    };
    document.addEventListener(CartErrorEvent.eventName, this.#boundShowCartError);

    // Real upload progress wiring (CRO 2026-04-20 — tier 2).
    // Upgrades the fake CSS tween to a live bar driven by XHR.upload.onprogress.
    // `pet-flow:upload-progress` fires while bytes are in flight; `…complete`
    // fires once the upload finishes but the server is still processing.
    this.#boundUploadProgress = (/** @type {Event} */ event) => {
      if (!(event instanceof CustomEvent)) return;
      this.#applyUploadProgress(event.detail?.percent);
    };
    this.#boundUploadComplete = (/** @type {Event} */ event) => {
      if (!(event instanceof CustomEvent)) return;
      this.#applyUploadProgress(event.detail?.percent ?? 95);
    };
    document.addEventListener('pet-flow:upload-progress', this.#boundUploadProgress);
    document.addEventListener('pet-flow:upload-complete', this.#boundUploadComplete);

    // URL-sync: the base theme's variant-picker replaceState call silently fails
    // in some contexts. As a safety net, listen for variant:selected and update
    // the URL ourselves after a short yield so the variant-picker's own attempt
    // (if it succeeds) runs first.
    this.#urlSyncAbort?.abort();
    this.#urlSyncAbort = new AbortController();
    const variantPicker = this.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener(
        ThemeEvents.variantSelected,
        (event) => {
          const vp = this.querySelector('variant-picker');
          if (!vp || vp.dataset.templateProductMatch !== 'true') return;
          const optionValueId = event.detail?.resource?.id;
          const selectedInput = optionValueId
            ? vp.querySelector(`fieldset input[data-option-value-id="${optionValueId}"]`)
            : null;
          if (!selectedInput) return;
          const variantId = selectedInput.dataset.variantId;
          const productUrl = selectedInput.dataset.connectedProductUrl || vp.dataset.productUrl;
          const url = new URL(window.location.href);
          if (variantId) {
            url.searchParams.set('variant', variantId);
          } else {
            url.searchParams.delete('variant');
          }
          if (productUrl && productUrl !== url.pathname.split('?')[0]) {
            url.pathname = productUrl;
          }
          if (url.href !== window.location.href) {
            setTimeout(() => {
              if (url.href !== window.location.href) {
                history.replaceState({}, '', url.toString());
              }
            }, 50);
          }
        },
        { signal: this.#urlSyncAbort.signal }
      );
    }

    // --- Sticky mobile CTA ---
    // Show a minimal subtotal + Next bar when the inline next button scrolls
    // offscreen (W&W pattern). Mobile-only via CSS; this JS wires the
    // visibility trigger, click handler, and price-sync on variant changes.
    this.#setupStickyCta();

    // --- Pin initial preview to White Frame + White Background ---
    // Per product spec, the preview image must ALWAYS start on the white/white
    // variant — not whatever Shopify picks as "first available." This runs
    // once on first connect, only if the URL doesn't already pin a specific
    // variant via ?variant=... (respect deep links into specific variants).
    this.#pinInitialWhiteWhite();

    // --- Image Preloading ---
    // Once the page is idle, quietly prefetch all product variant images
    // so they load instantly when users switch options.
    this.#scheduleImagePreload();
  }

  /**
   * Forces the preview to the White Frame + White Background variant on
   * initial page load. Finds each option group whose name handleize matches
   * a "color/background" role, looks for a "White" / "Ivory" / equivalent
   * value, and if it isn't already checked, programmatically clicks it.
   * The click goes through variant-picker's normal change pipeline → same
   * optimistic-update path the user would trigger.
   *
   * Skipped when the URL has ?variant=... so deep links stay honored.
   * Skipped on subsequent morphs (only runs once per element lifetime).
   */
  #pinInitialWhiteWhite() {
    if (this.#initialWhiteWhitePinned) return;
    this.#initialWhiteWhitePinned = true;

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('variant')) return;
    } catch {
      /* noop */
    }

    if (!this.#optionGroups || this.#optionGroups.length === 0) return;

    // Heuristic for which option groups control the "frame" and "background".
    // Match on the group's data-step-label or legend text — works across
    // localizations that keep the English "color"/"background" roots.
    const findGroupForRole = (keywords) => {
      for (const group of this.#optionGroups ?? []) {
        const label =
          (group.getAttribute('data-step-label') || '').toLowerCase() +
          ' ' +
          (group.querySelector('legend')?.textContent || '').toLowerCase();
        if (keywords.some((kw) => label.includes(kw))) return group;
      }
      return null;
    };

    const groups = [
      { group: findGroupForRole(['frame']), preferred: ['white'] },
      { group: findGroupForRole(['background', 'color']), preferred: ['white'] },
    ];

    for (const { group, preferred } of groups) {
      if (!group) continue;
      const radios = /** @type {HTMLInputElement[]} */ (
        Array.from(group.querySelectorAll('input[type="radio"]'))
      );
      if (radios.length === 0) continue;

      const checked = radios.find((r) => r.checked);
      const target = radios.find((r) => {
        const v = (r.value || '').toLowerCase();
        return preferred.some((p) => v === p || v.startsWith(p + ' ') || v.includes(p));
      });

      // Already on the preferred value, or no preferred value exists for this group.
      if (!target || target === checked) continue;

      // Only pin if the preferred variant is actually available — don't strand
      // the user on a sold-out option.
      if (target.getAttribute('aria-disabled') === 'true' ||
          target.dataset.optionAvailable === 'false') {
        continue;
      }

      target.click();
    }
  }

  disconnectedCallback() {
    if (this.#nextButton) {
      this.#nextButton.removeEventListener('click', this.#handleNextClick);
    }
    if (this.#previousButton) {
      this.#previousButton.removeEventListener('click', this.#handlePrevClick);
    }
    if (this.#petInputs) {
      this.#petInputs.forEach((input) => {
        input.removeEventListener('change', this.#handlePetChange);
      });
    }
    if (this.#imageInputs) {
      this.#imageInputs.forEach((input) => {
        input.removeEventListener('change', this.#handleImageChange);
      });
    }
    if (this.#nameInputs) {
      this.#nameInputs.forEach((input) => {
        input.removeEventListener('input', this.#handleNameChange);
      });
    }
    if (this.#variantUpdateTarget) {
      this.#variantUpdateTarget.removeEventListener(ThemeEvents.variantUpdate, this.#boundVariantUpdateHandler);
    }
    if (this.#boundClearLoading) {
      document.removeEventListener(CartAddEvent.eventName, this.#boundClearLoading);
      document.removeEventListener(CartErrorEvent.eventName, this.#boundClearLoading);
      this.#boundClearLoading = null;
    }
    if (this.#boundUploadProgress) {
      document.removeEventListener('pet-flow:upload-progress', this.#boundUploadProgress);
      this.#boundUploadProgress = null;
    }
    if (this.#boundUploadComplete) {
      document.removeEventListener('pet-flow:upload-complete', this.#boundUploadComplete);
      this.#boundUploadComplete = null;
    }
    if (this.#boundShowCartError) {
      document.removeEventListener(CartErrorEvent.eventName, this.#boundShowCartError);
      this.#boundShowCartError = null;
    }
    if (this.#errorHideTimeoutId != null) {
      clearTimeout(this.#errorHideTimeoutId);
      this.#errorHideTimeoutId = null;
    }
    if (this.#dropzoneAbort) {
      this.#dropzoneAbort.abort();
      this.#dropzoneAbort = null;
    }
    if (this.#stickyCtaObserver) {
      this.#stickyCtaObserver.disconnect();
      this.#stickyCtaObserver = null;
    }
    if (this.#boundStickyPriceSync) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#boundStickyPriceSync);
      this.#boundStickyPriceSync = null;
    }
    this.#urlSyncAbort?.abort();
  }

  // ──────────────────────────────────────────────
  // Step Indicator
  // ──────────────────────────────────────────────

  /**
   * Finds the step indicator element and caches references to its children.
   * @param {string | undefined} productId
   */
  #initStepIndicator(productId) {
    if (!productId) return;
    this.#indicatorRoot = document.querySelector(
      `[data-pet-flow-indicator][data-product-id="${productId}"]`
    );
    if (!this.#indicatorRoot) return;

    this.#indicatorLabel = this.#indicatorRoot.querySelector('[data-pet-flow-step-label]');
    this.#indicatorSummary = this.#indicatorRoot.querySelector('[data-pet-flow-summary]');
    this.#indicatorDots = /** @type {HTMLElement[]} */ (
      Array.from(this.#indicatorRoot.querySelectorAll('[data-indicator-step]'))
    );
    this.#indicatorConnectors = /** @type {HTMLElement[]} */ (
      Array.from(this.#indicatorRoot.querySelectorAll('.pet-flow-indicator__connector'))
    );

    // Build step labels from dot data attributes
    this.#indicatorDots.forEach((dot) => {
      const stepKey = dot.dataset.indicatorStep;
      const label = dot.dataset.stepLabel;
      if (stepKey && label) {
        // Convert option name → friendly label
        const lowerLabel = label.toLowerCase();
        if (lowerLabel.includes('background') || lowerLabel.includes('color') || lowerLabel.includes('colour')) {
          this.#stepLabels[stepKey] = `Choose your ${label.toLowerCase()}`;
        } else if (lowerLabel === 'size') {
          this.#stepLabels[stepKey] = 'Choose your frame size';
        } else if (lowerLabel.includes('frame')) {
          this.#stepLabels[stepKey] = `Choose your ${label.toLowerCase()}`;
        } else {
          this.#stepLabels[stepKey] = label;
        }
      }
    });
  }

  /**
   * Updates the step indicator dots, label, and selection summary.
   */
  #updateStepIndicator() {
    if (!this.#indicatorRoot) return;

    const currentStep = this.#currentMaxIndex;
    const optionCount = this.#optionGroups?.length ?? 0;
    const onPetDetailsStep = currentStep === optionCount && optionCount > 0;

    // Determine the step key for the current step
    let currentStepKey;
    if (currentStep < 0) {
      currentStepKey = '-1';
    } else if (onPetDetailsStep) {
      currentStepKey = 'details';
    } else {
      currentStepKey = String(currentStep);
    }

    // Update dots
    let reachedCurrent = false;
    this.#indicatorDots.forEach((dot) => {
      const stepKey = dot.dataset.indicatorStep || '';
      const isActive = stepKey === currentStepKey;
      const isCompleted = this.#isStepCompleted(stepKey, currentStepKey);

      dot.classList.toggle('pet-flow-indicator__dot--active', isActive);
      dot.classList.toggle('pet-flow-indicator__dot--completed', isCompleted);

      if (isActive) reachedCurrent = true;
    });

    // Update connectors (completed if the step AFTER the connector is completed or active)
    this.#indicatorConnectors.forEach((conn, i) => {
      // Connector i sits between dot i and dot i+1
      const nextDot = this.#indicatorDots[i + 1];
      if (!nextDot) return;
      const nextStepKey = nextDot.dataset.indicatorStep || '';
      const nextIsCompleted = this.#isStepCompleted(nextStepKey, currentStepKey);
      const nextIsActive = nextStepKey === currentStepKey;
      conn.classList.toggle('pet-flow-indicator__connector--completed', nextIsCompleted || nextIsActive);
    });

    // Update label
    if (this.#indicatorLabel) {
      const label = this.#stepLabels[currentStepKey] || '';
      this.#indicatorLabel.textContent = label;
    }

    // Update summary chips
    this.#updateSelectionSummary(currentStepKey);
  }

  /**
   * @param {string} stepKey
   * @param {string} currentStepKey
   * @returns {boolean}
   */
  #isStepCompleted(stepKey, currentStepKey) {
    const stepOrder = ['-1'];
    for (let i = 0; i < (this.#optionGroups?.length ?? 0); i++) {
      stepOrder.push(String(i));
    }
    stepOrder.push('details');

    const stepIndex = stepOrder.indexOf(stepKey);
    const currentIndex = stepOrder.indexOf(currentStepKey);
    return stepIndex >= 0 && currentIndex >= 0 && stepIndex < currentIndex;
  }

  /**
   * Selection-summary chips were retired 2026-04-21 (CRO: preview renders
   * every choice already; chips were redundant visual noise). Kept as a
   * no-op so the rest of the step-indicator pipeline doesn't have to care.
   * @param {string} _currentStepKey
   */
  #updateSelectionSummary(_currentStepKey) {
    if (this.#indicatorSummary && this.#indicatorSummary.innerHTML !== '') {
      this.#indicatorSummary.innerHTML = '';
    }
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ──────────────────────────────────────────────
  // Mobile inline preview (Option B)
  // ──────────────────────────────────────────────

  /**
   * Finds the mobile preview container and image element within this
   * pet-variant-flow, and caches references.
   */
  #initMobilePreview() {
    const el = this.querySelector('[data-mobile-preview]');
    if (el instanceof HTMLElement) {
      this.#mobilePreview = el;
      const img = el.querySelector('.mobile-inline-preview__image');
      if (img instanceof HTMLImageElement) {
        this.#mobilePreviewImg = img;
      }
    }
  }

  /**
   * Shows or hides the mobile preview based on the current step.
   * Visible during option steps (0+) and pet-details step, hidden
   * during pet selection (step -1) and on desktop (CSS handles that).
   */
  #updateMobilePreviewVisibility() {
    if (!this.#mobilePreview) return;
    const shouldShow = this.#currentMaxIndex >= 0;
    this.#mobilePreview.hidden = !shouldShow;
    this.#mobilePreview.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  /**
   * Updates the mobile preview image to match the gallery. Applies a
   * short crossfade if the src actually changes.
   * @param {string} [imageUrl] — if provided, use this URL directly;
   *   otherwise reads from the main media-gallery <img>.
   */
  #updateMobilePreviewImage(imageUrl) {
    if (!this.#mobilePreviewImg) return;

    // If no URL passed, try to read the current gallery image
    if (!imageUrl) {
      const section = this.closest('.shopify-section');
      const gallery = section?.querySelector('media-gallery');
      if (gallery) {
        const galleryImg = gallery.querySelector('img');
        if (galleryImg instanceof HTMLImageElement && galleryImg.src) {
          imageUrl = galleryImg.src;
        }
      }
    }

    if (!imageUrl) return;

    // Don't update if already showing this image
    const currentSrc = this.#mobilePreviewImg.src;
    if (currentSrc && new URL(currentSrc, location.href).pathname === new URL(imageUrl, location.href).pathname) {
      return;
    }

    // Crossfade: fade out → swap src → fade in on load
    const img = this.#mobilePreviewImg;
    img.classList.add('mobile-inline-preview__image--fading');

    const swapSrc = () => {
      img.src = imageUrl;
      img.removeAttribute('srcset');

      const fadeIn = () => {
        img.classList.remove('mobile-inline-preview__image--fading');
      };

      // If already cached, onload fires synchronously in some browsers
      if (img.complete) {
        requestAnimationFrame(fadeIn);
      } else {
        img.addEventListener('load', fadeIn, { once: true });
        // Safety: remove fading class after timeout in case load never fires
        setTimeout(fadeIn, 800);
      }
    };

    // Wait for the fade-out transition to finish (250ms in CSS)
    setTimeout(swapSrc, 250);
  }

  // ──────────────────────────────────────────────
  // Image preloading
  // ──────────────────────────────────────────────

  /**
   * Initialises the mobile sticky CTA (subtotal + "Next / Add to Cart"
   * bar that slides up when the inline next button scrolls offscreen).
   *
   * - Visibility: IntersectionObserver on the inline `.pet-flow__next-button`.
   *   When it leaves the viewport, show the sticky bar. Hide again on return.
   * - Click: delegates to the inline next button's click handler, so there's
   *   only ever one path through `#handleNextClick` / `#submitAddToCart`.
   * - Price / label sync: mirrors whatever the inline button text currently
   *   says ("Next", "Add to Cart", "Adding…", "Added!") and whatever the
   *   main product-price element shows, updating on variant:update.
   */
  #setupStickyCta() {
    const section = this.closest('.shopify-section, dialog, product-card');
    const productId = this.dataset.productId;
    if (!section || !productId) return;

    const sticky = /** @type {HTMLElement | null} */ (
      section.querySelector(`[data-pet-sticky-cta][data-product-id="${productId}"]`)
    );
    if (!sticky) return;

    this.#stickyCta = sticky;
    this.#stickyCtaButton = /** @type {HTMLButtonElement | null} */ (
      sticky.querySelector('[data-pet-sticky-next]')
    );
    this.#stickyCtaButtonTextEl = /** @type {HTMLElement | null} */ (
      sticky.querySelector('[data-pet-sticky-next-text]')
    );
    this.#stickyCtaPriceEl = /** @type {HTMLElement | null} */ (
      sticky.querySelector('[data-pet-sticky-price]')
    );

    // Click → smooth-scroll back up to the customization controls, DO NOT
    // trigger the next step. The whole point of the sticky is to get the
    // user back to the form so they can continue from where they left off.
    this.#stickyCtaButton?.addEventListener('click', (event) => {
      event.preventDefault();
      const target = this.#nextButton || this.querySelector('[data-pet-flow-nav]');
      if (target instanceof HTMLElement) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Fallback: scroll to this component.
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    // Initial text + price sync.
    this.#syncStickyCtaFromInline();

    // Re-sync whenever variant changes (price + button-label may change).
    this.#boundStickyPriceSync = () => this.#syncStickyCtaFromInline();
    document.addEventListener(ThemeEvents.variantUpdate, this.#boundStickyPriceSync);

    // Wire visibility observer on the inline Next button. Class-only model:
    // .is-visible controls pointer-events, opacity, and transform — no
    // [hidden] attribute toggling, no setTimeout races. The element is
    // always mounted in the DOM on mobile; CSS media query hides it on
    // desktop.
    this.#attachStickyObserver();
  }

  /**
   * (Re)attaches the IntersectionObserver that drives sticky visibility.
   * Called on connect and again after any variant-update that may have
   * morphed the inline Next button out of the DOM.
   */
  #attachStickyObserver() {
    if (!this.#stickyCta || !this.#nextButton) return;
    if (!('IntersectionObserver' in window)) {
      // Fallback: just show it always on mobile.
      this.#stickyCta.classList.add('is-visible');
      this.#stickyCta.setAttribute('aria-hidden', 'false');
      return;
    }

    // Disconnect any prior observer pointed at a stale element.
    if (this.#stickyCtaObserver) {
      this.#stickyCtaObserver.disconnect();
      this.#stickyCtaObserver = null;
    }

    const sticky = this.#stickyCta;
    this.#stickyCtaObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Show the sticky only when the inline button is ABOVE the
          // viewport (scrolled past). Don't show when it's still below
          // (user hasn't scrolled to it yet).
          const isAboveViewport = entry.boundingClientRect.top < 0;
          const shouldShow = !entry.isIntersecting && isAboveViewport;
          sticky.classList.toggle('is-visible', shouldShow);
          sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        }
      },
      { threshold: 0 }
    );
    this.#stickyCtaObserver.observe(this.#nextButton);
  }

  /**
   * Mirrors the inline next-button label into the sticky CTA, and syncs the
   * sticky price from the main product-price element. Cheap; called on init
   * and on variant:update.
   */
  #syncStickyCtaFromInline() {
    if (!this.#stickyCta) return;

    // Button label — mirror inline text.
    const inlineText = this.#nextButton?.querySelector('.pet-flow__next-button-text')?.textContent?.trim();
    if (inlineText && this.#stickyCtaButtonTextEl) {
      this.#stickyCtaButtonTextEl.textContent = inlineText;
    }

    // Disabled mirror.
    if (this.#stickyCtaButton) {
      this.#stickyCtaButton.disabled = Boolean(this.#nextButton?.disabled);
    }

    // Price — read from main product-price .price element rendered by theme.
    const section = this.closest('.shopify-section, dialog, product-card');
    const priceEl = section?.querySelector('product-price .price');
    const priceText = priceEl?.textContent?.trim();
    if (priceText && this.#stickyCtaPriceEl) {
      this.#stickyCtaPriceEl.textContent = priceText;
    }
  }

  /**
   * Schedules background preloading of all variant product images once
   * the page is idle. Uses requestIdleCallback (with setTimeout fallback)
   * so we don't compete with LCP or other critical resources.
   */
  #scheduleImagePreload() {
    const startPreload = () => {
      // Wait an additional 2 seconds after idle to give LCP/FCP
      // plenty of breathing room on slow connections.
      setTimeout(() => this.#preloadVariantImages(), 2000);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(startPreload, { timeout: 8000 });
    } else {
      // Fallback for Safari (no requestIdleCallback)
      setTimeout(startPreload, 4000);
    }
  }

  /**
   * Reads the variant-images JSON blob (all variant preview images for
   * all pet products) plus gallery srcset values, and preloads everything
   * so option switching feels instant.
   */
  #preloadVariantImages() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const urls = new Set();

    // 1. Gather ALL variant images from the variant-images JSON blob
    //    This covers every background-color × frame-colour combination
    //    across all 4 pet products — the full set of preview images.
    const variantImagesScript = section.querySelector('script[data-variant-images]');
    if (variantImagesScript) {
      try {
        const variantImagesMap = JSON.parse(variantImagesScript.textContent || '{}');
        for (const key of Object.keys(variantImagesMap)) {
          const imageList = variantImagesMap[key];
          if (Array.isArray(imageList)) {
            imageList.forEach((url) => { if (url) urls.add(url); });
          }
        }
      } catch (e) {
        // Silently ignore parse errors
      }
    }

    // 2. Fallback: also gather featured images from pet-products-map
    const jsonScript = section.querySelector('script[data-pet-products-map]');
    if (jsonScript) {
      try {
        const productsMap = JSON.parse(jsonScript.textContent || '{}');
        for (const key of Object.keys(productsMap)) {
          const product = productsMap[key];
          if (product.featuredImage) {
            urls.add(product.featuredImage);
          }
        }
      } catch (e) {
        // Silently ignore parse errors
      }
    }

    // 3. Gather gallery images and srcset for current product
    const gallery = section.querySelector('media-gallery');
    if (gallery) {
      gallery.querySelectorAll('img').forEach((img) => {
        if (img instanceof HTMLImageElement) {
          if (img.src) urls.add(img.src);
          const srcset = img.getAttribute('srcset');
          if (srcset) {
            srcset.split(',').forEach((entry) => {
              const url = entry.trim().split(/\s+/)[0];
              if (url) urls.add(url);
            });
          }
        }
      });
      gallery.querySelectorAll('source[srcset]').forEach((source) => {
        const srcset = source.getAttribute('srcset');
        if (srcset) {
          srcset.split(',').forEach((entry) => {
            const url = entry.trim().split(/\s+/)[0];
            if (url) urls.add(url);
          });
        }
      });
    }

    if (urls.size === 0) return;

    // 3. Preload images using <link rel="preload"> for browser-native
    //    priority handling, falling back to Image() for older browsers.
    const urlArray = Array.from(urls);
    let index = 0;

    const preloadNext = () => {
      if (index >= urlArray.length) return;
      const url = urlArray[index++];

      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          this.#preloadSingleImage(url);
          preloadNext();
        }, { timeout: 3000 });
      } else {
        // Stagger loads by 100ms to avoid saturating bandwidth
        setTimeout(() => {
          this.#preloadSingleImage(url);
          preloadNext();
        }, 100);
      }
    };

    preloadNext();
  }

  /**
   * Preloads a single image URL.
   * @param {string} url
   */
  #preloadSingleImage(url) {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }

  // ──────────────────────────────────────────────
  // Option group visibility
  // ──────────────────────────────────────────────

  #collectOptionGroups() {
    const variantPicker = this.querySelector('variant-picker');
    if (!variantPicker) {
      this.#optionGroups = [];
      return;
    }
    this.#optionGroups = /** @type {HTMLElement[]} */ (
      Array.from(variantPicker.querySelectorAll('.variant-picker__form .variant-option'))
    );
  }

  #hideAllOptionGroups() {
    if (!this.#optionGroups) return;
    this.#optionGroups.forEach((group) => {
      group.dataset.petFlowHidden = 'true';
    });
  }

  #updateOptionVisibility() {
    if (!this.#optionGroups) return;
    const optionCount = this.#optionGroups.length;
    const onPetDetailsStep = this.#currentMaxIndex >= optionCount;
    this.#optionGroups.forEach((group, index) => {
      const isVisible =
        !onPetDetailsStep &&
        this.#currentMaxIndex >= 0 &&
        index === this.#currentMaxIndex;
      group.dataset.petFlowHidden = isVisible ? 'false' : 'true';
    });
  }

  // ──────────────────────────────────────────────
  // Pet count helpers
  // ──────────────────────────────────────────────

  #hasPetSelection() {
    if (!this.#petInputs) return false;
    return Array.from(this.#petInputs).some((input) => input.checked);
  }

  /**
   * @returns {number} 1–4 based on selected pet count, or 0 if none selected
   */
  #getSelectedPetCount() {
    const checked = Array.from(this.#petInputs ?? []).find((input) => input.checked);
    const petValue = String(checked?.dataset?.petCountValue || '');
    if (!petValue || !petValue.endsWith('pet')) return 0;
    const firstChar = petValue.charAt(0);
    const numeric = parseInt(firstChar, 10);
    if (!Number.isFinite(numeric)) return 0;
    return Math.min(4, Math.max(1, numeric));
  }

  // ──────────────────────────────────────────────
  // Pet details visibility
  // ──────────────────────────────────────────────

  #updatePetDetailsVisibility() {
    const productId = this.dataset.productId;
    if (!productId) return;
    const container = /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-pet-details-container][data-product-id="${productId}"]`)
    );
    if (!container) return;

    const count = this.#getSelectedPetCount();
    const optionCount = this.#optionGroups?.length ?? 0;
    const onPetDetailsStep = optionCount > 0 && this.#currentMaxIndex === optionCount;

    if (onPetDetailsStep && count > 0) {
      container.hidden = false;
      container.dataset.petFlowVisible = 'true';
      container.querySelectorAll('[data-pet-details-row]').forEach((row) => {
        const rowNum = parseInt(row.getAttribute('data-pet-details-row') || '0', 10);
        const isVisible = rowNum >= 1 && rowNum <= count;
        const rowEl = /** @type {HTMLElement} */ (row);
        rowEl.hidden = !isVisible;
        rowEl.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        const nameInput = row.querySelector('input[data-pet-details-name]');
        const imageInput = row.querySelector('input[data-pet-details-image]');
        // Disabled inputs are skipped by FormData, so hidden pet rows won't
        // submit empty `Pet N Image` / `Pet N Name` line-item properties.
        if (nameInput instanceof HTMLInputElement) nameInput.disabled = !isVisible;
        if (imageInput instanceof HTMLInputElement) imageInput.disabled = !isVisible;
        if (!isVisible) {
          if (nameInput instanceof HTMLInputElement) nameInput.value = '';
          if (imageInput instanceof HTMLInputElement) imageInput.value = '';
          const fileNameEl = row.querySelector('[data-pet-image-file-name]');
          if (fileNameEl instanceof HTMLElement) {
            fileNameEl.textContent = '';
            fileNameEl.hidden = true;
          }
        }
      });
      container.setAttribute('data-pet-details-count', String(count));
    } else if (count > 0) {
      container.hidden = true;
      container.dataset.petFlowVisible = 'false';
      container.setAttribute('data-pet-details-count', String(count));
      // Keep disabled state in sync with the selected count so inputs are
      // excluded from FormData whenever the container isn't actively shown.
      container.querySelectorAll('[data-pet-details-row]').forEach((row) => {
        const rowNum = parseInt(row.getAttribute('data-pet-details-row') || '0', 10);
        const isActive = rowNum >= 1 && rowNum <= count;
        const nameInput = row.querySelector('input[data-pet-details-name]');
        const imageInput = row.querySelector('input[data-pet-details-image]');
        if (nameInput instanceof HTMLInputElement) nameInput.disabled = !isActive;
        if (imageInput instanceof HTMLInputElement) imageInput.disabled = !isActive;
      });
    } else {
      container.hidden = true;
      container.dataset.petFlowVisible = 'false';
      container.removeAttribute('data-pet-details-count');
      container.querySelectorAll('[data-pet-details-row]').forEach((row) => {
        const rowEl = /** @type {HTMLElement} */ (row);
        rowEl.hidden = true;
        const fileNameEl = rowEl.querySelector('[data-pet-image-file-name]');
        if (fileNameEl instanceof HTMLElement) {
          fileNameEl.textContent = '';
          fileNameEl.hidden = true;
        }
      });
      container.querySelectorAll('input[data-pet-details-name], input[data-pet-details-image]').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.value = '';
          input.disabled = true;
        }
      });
    }
  }

  // ──────────────────────────────────────────────
  // Button state
  // ──────────────────────────────────────────────

  #updateNextButtonState() {
    if (!this.#nextButton || !this.#optionGroups) return;
    const hasPet = this.#hasPetSelection();
    const hasOptions = this.#optionGroups.length > 0;
    const optionCount = this.#optionGroups.length;
    const maxStepIndex = optionCount; // pet-details step is now the final step
    const onPetDetailsStep = optionCount > 0 && this.#currentMaxIndex === optionCount;
    const petImagesComplete = this.#areVisiblePetImagesComplete();
    const petNamesComplete = this.#areVisiblePetNamesComplete();

    const textSpan = this.#nextButton.querySelector('.pet-flow__next-button-text');
    const iconSpan = this.#nextButton.querySelector('.pet-flow__next-button-icon');

    if (this.#currentMaxIndex < 0) {
      this.#nextButton.disabled = !(hasPet && hasOptions);
      if (textSpan instanceof HTMLElement) {
        textSpan.textContent = this.#nextButtonDefaultLabel;
      }
      if (iconSpan instanceof HTMLElement) {
        iconSpan.hidden = false;
      }
    } else if (onPetDetailsStep) {
      // Final step: button becomes "Add to Cart", enabled when images are complete.
      // Pet names are OPTIONAL (CRO 2026-04-20) — they don't gate ATC.
      // petNamesComplete is still computed (for analytics/future use) but not enforced here.
      this.#nextButton.disabled = !petImagesComplete;
      if (textSpan instanceof HTMLElement) {
        const addToCartLabel =
          (typeof Theme !== 'undefined' && Theme.translations?.add_to_cart) || 'Add to Cart';
        textSpan.textContent = addToCartLabel;
      }
      if (iconSpan instanceof HTMLElement) {
        iconSpan.hidden = true;
      }
    } else {
      const canShowMore = this.#currentMaxIndex < maxStepIndex;
      this.#nextButton.disabled = !canShowMore;
      if (textSpan instanceof HTMLElement) {
        textSpan.textContent = this.#nextButtonDefaultLabel;
      }
      if (iconSpan instanceof HTMLElement) {
        iconSpan.hidden = false;
      }
    }
  }

  #updatePrevButtonState() {
    if (!this.#previousButton) return;
    const onPetStep = this.#currentMaxIndex < 0;
    this.#previousButton.hidden = onPetStep;
    this.#previousButton.disabled = onPetStep;
  }

  // ──────────────────────────────────────────────
  // UI sync
  // ──────────────────────────────────────────────

  #syncUiFromState() {
    // Pet selector: use data attribute for animated transitions
    if (this.#petSelectorRoot) {
      const showPets = this.#currentMaxIndex < 0;
      this.#petSelectorRoot.hidden = !showPets;
      this.#petSelectorRoot.dataset.petFlowVisible = showPets ? 'true' : 'false';
    }
    this.#updatePetDetailsVisibility();
    this.#updatePrevButtonState();
    this.#updateNextButtonState();
    this.#updateStepIndicator();
    this.#updateMobilePreviewVisibility();
    // Keep sticky mobile CTA in sync with inline button label/disabled state.
    if (this.#stickyCta) this.#syncStickyCtaFromInline();
  }

  // ──────────────────────────────────────────────
  // Validation helpers
  // ──────────────────────────────────────────────

  #areVisiblePetImagesComplete() {
    const productId = this.dataset.productId;
    if (!productId) return true;
    const container = /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-pet-details-container][data-product-id="${productId}"]`)
    );
    if (!container || container.hidden) return true;

    const rows = Array.from(container.querySelectorAll('[data-pet-details-row]')).filter((row) => {
      const rowEl = /** @type {HTMLElement} */ (row);
      return !rowEl.hidden;
    });

    if (!rows.length) return true;

    return rows.every((row) => {
      const input = row.querySelector('input[data-pet-details-image]');
      if (!(input instanceof HTMLInputElement)) return false;
      return !!input.files && input.files.length > 0;
    });
  }

  #areVisiblePetNamesComplete() {
    const productId = this.dataset.productId;
    if (!productId) return true;
    const container = /** @type {HTMLElement | null} */ (
      document.querySelector(`[data-pet-details-container][data-product-id="${productId}"]`)
    );
    if (!container || container.hidden) return true;

    const rows = Array.from(container.querySelectorAll('[data-pet-details-row]')).filter((row) => {
      const rowEl = /** @type {HTMLElement} */ (row);
      return !rowEl.hidden;
    });

    if (!rows.length) return true;

    return rows.every((row) => {
      const input = row.querySelector('input[data-pet-details-name]');
      if (!(input instanceof HTMLInputElement)) return false;
      return input.value.trim().length > 0;
    });
  }

  // ──────────────────────────────────────────────
  // Event handlers
  // ──────────────────────────────────────────────

  #handleNextClick = () => {
    if (!this.#optionGroups || !this.#hasPetSelection()) return;

    const optionCount = this.#optionGroups.length;
    const maxStepIndex = optionCount; // pet-details step is now the final step
    const onPetDetailsStep = optionCount > 0 && this.#currentMaxIndex === maxStepIndex;

    // Final step: submit the product form → fires ATC → cart drawer auto-opens.
    if (onPetDetailsStep) {
      this.#submitAddToCart();
      return;
    }

    if (this.#currentMaxIndex < 0) {
      this.#currentMaxIndex = 0;
    } else if (this.#currentMaxIndex < maxStepIndex) {
      this.#currentMaxIndex += 1;
    } else {
      return;
    }

    this.#updateOptionVisibility();
    this.#syncUiFromState();

    // Warm /checkout as soon as the user reaches the upload step. Upload +
    // ATC + cart-drawer-open takes 3-8s, which is enough time for the
    // browser to fetch + parse the checkout HTML into its cache. By the
    // time the user clicks Checkout in the drawer, first-byte is instant.
    // Passive `<link rel="prefetch">` — no pixel side effects.
    if (this.#currentMaxIndex === maxStepIndex) {
      prefetchCheckoutOnce();
    }
  };

  #handlePetChange = () => {
    if (this.#currentMaxIndex >= 0) {
      this.#updateOptionVisibility();
    }
    this.#syncUiFromState();
  };

  /**
   * @param {Event & { target: EventTarget | null }} event
   */
  #handleImageChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const row = input.closest('[data-pet-details-row]');
    if (!row) return;
    const fileNameEl = row.querySelector('[data-pet-image-file-name]');
    if (!(fileNameEl instanceof HTMLElement)) return;

    const originalFile = input.files && input.files[0];

    if (!originalFile) {
      fileNameEl.textContent = '';
      fileNameEl.hidden = true;
      this.#updateNextButtonState();
      return;
    }

    // Show filename immediately — feedback must not wait on compression.
    fileNameEl.textContent = originalFile.name;
    fileNameEl.hidden = false;
    this.#updateNextButtonState();

    // Compress in the background while the user keeps filling the form.
    // By the time they hit Add to Cart, the input carries a small JPEG
    // instead of a 10MB phone photo, cutting upload time 10–50x.
    try {
      const compressed = await this.#compressImageFile(originalFile);
      if (compressed && compressed !== originalFile) {
        const dt = new DataTransfer();
        dt.items.add(compressed);
        input.files = dt.files;
        // Update the visible filename to match the new extension (.jpg).
        fileNameEl.textContent = compressed.name;
      }
    } catch (err) {
      // Fall back silently to the original file. Not user-visible.
      console.warn('[pet-flow] image compression skipped:', err);
    }
  };

  /**
   * Client-side image compression, tuned to preserve the reference quality
   * our portrait supplier needs.
   *
   * Targets ONLY genuinely huge phone photos (>3MB). Anything smaller is
   * passed through untouched. Re-encode keeps 3000px on the longest edge
   * at JPEG q=0.92 — far above what any artist or AI pipeline consumes
   * as input reference. Falls back to original on any decode error.
   *
   * Typical outcome: a 12MB iPhone Pro photo becomes ~1.5–2MB at 3000px,
   * a ~6x upload speedup on cellular. Visual quality at the pet's face
   * is indistinguishable from the original in blind comparison.
   *
   * Analytics-safe: Shopify still sees the same ATC event, same variant,
   * same quantity — only the bytes in the line-item-property file change.
   *
   * @param {File} file
   * @returns {Promise<File>}
   */
  async #compressImageFile(file) {
    if (!file.type.startsWith('image/')) return file;
    // Don't touch files that are already reasonably sized. The goal is to
    // kill 10MB+ phone photos, not re-encode every upload.
    if (file.size < 3 * 1024 * 1024) return file;

    const MAX_DIMENSION = 3000;
    const JPEG_QUALITY = 0.92;

    /** @type {ImageBitmap | HTMLImageElement | null} */
    let source = null;
    try {
      if (typeof createImageBitmap === 'function') {
        source = await createImageBitmap(file);
      } else {
        source = await new Promise((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('image decode failed'));
          };
          img.src = url;
        });
      }

      const w0 = /** @type {any} */ (source).width;
      const h0 = /** @type {any} */ (source).height;
      if (!w0 || !h0) return file;

      const ratio = Math.min(1, MAX_DIMENSION / Math.max(w0, h0));
      // Nothing to gain: dimensions already within our max AND bytes
      // already modest — hand the original through unchanged.
      if (ratio >= 1 && file.size < 5 * 1024 * 1024) {
        /** @type {any} */ (source).close?.();
        return file;
      }

      const w = Math.round(w0 * ratio);
      const h = Math.round(h0 * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(/** @type {any} */ (source), 0, 0, w, h);
      /** @type {any} */ (source).close?.();

      /** @type {Blob | null} */
      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY)
      );
      if (!blob || blob.size >= file.size) return file;

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'pet';
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    } catch (err) {
      /** @type {any} */ (source)?.close?.();
      throw err;
    }
  }

  #handleNameChange = () => {
    this.#updateNextButtonState();
  };

  #handlePrevClick = () => {
    if (!this.#optionGroups) return;

    const optionCount = this.#optionGroups.length;
    if (this.#currentMaxIndex > 0) {
      this.#currentMaxIndex -= 1;
      this.#updateOptionVisibility();
    } else if (this.#currentMaxIndex === 0) {
      this.#currentMaxIndex = -1;
      this.#hideAllOptionGroups();
    } else if (this.#currentMaxIndex >= optionCount) {
      // Safety net — if we ever land past the last step, snap back to it.
      this.#currentMaxIndex = optionCount - 1;
      this.#updateOptionVisibility();
    }

    this.#syncUiFromState();
  };

  // ──────────────────────────────────────────────
  // Add to Cart
  // ──────────────────────────────────────────────

  /**
   * Submits the product form for the currently-selected pet-count variant.
   * Gives immediate "Adding…" feedback on the pet-flow button so the click
   * feels responsive while the multipart upload to /cart/add.js runs.
   *
   * The form's submit handler runs /cart/add.js, dispatches CartAddEvent,
   * and the cart-drawer picks up the event and slides open automatically.
   */
  #submitAddToCart() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const section = this.closest('.shopify-section');
    const productForm = /** @type {HTMLElement | null} */ (
      section?.querySelector(`product-form-component[data-product-id="${productId}"]`)
    );
    const form = productForm?.querySelector('form');

    if (!(form instanceof HTMLFormElement)) return;

    // Immediate UI feedback: disable button, swap label to "Adding…",
    // and add a loading class so CSS can render a spinner state. Cleared
    // by CartAddEvent / CartErrorEvent listeners on this component.
    this.#setNextButtonLoading(true);

    form.requestSubmit();
  }

  /** @type {number | null} Start time (ms) of the current progress tween. */
  #progressTweenStartMs = null;
  /** @type {number | null} rAF id for the current progress tween. */
  #progressTweenRafId = null;
  /** @type {number} Last committed progress percent (0–98). */
  #progressCurrent = 0;
  /** @type {number} Highest real-XHR percent seen this upload (0–100). */
  #progressRealMax = 0;

  /**
   * Kicks off the asymptotic fake progress tween (CRO 2026-04-20 rev 2).
   * Curve: `98 * (1 - e^(-t / tau))` — quick rise at first, smoothly
   * decelerating to ~98% and holding there. The user always sees the bar
   * "almost full" when the cart opens, regardless of whether their upload
   * took 2s or 12s. Real XHR progress, when available, is merged via
   * `#applyUploadProgress` and takes over if it's ahead of the tween.
   *
   * Tau tuning: tau=3500 gives ~25% at 1s, ~55% at 3s, ~82% at 7s, ~93%
   * at 11s, ~97% at 16s. Matches the observed 2–8s upload window for
   * typical pet photos on 4G/5G.
   */
  #startProgressTween() {
    this.#cancelProgressTween();
    this.#progressTweenStartMs = performance.now();
    this.#progressCurrent = 0;
    this.#progressRealMax = 0;
    const tau = 3500; // ms — time constant
    const asymptote = 98; // hard ceiling until success fires
    const step = () => {
      const button = this.#nextButton;
      if (!button || !button.classList.contains('is-loading')) {
        this.#cancelProgressTween();
        return;
      }
      const t = performance.now() - (this.#progressTweenStartMs || 0);
      const fake = asymptote * (1 - Math.exp(-t / tau));
      // Merge with real XHR progress (capped at asymptote). Real can never
      // snap BACKWARD — we keep the highest value we've ever painted.
      const merged = Math.max(fake, this.#progressRealMax * (asymptote / 100));
      const next = Math.max(this.#progressCurrent, Math.min(asymptote, merged));
      if (next > this.#progressCurrent + 0.05) {
        this.#progressCurrent = next;
        button.style.setProperty('--progress', next.toFixed(2) + '%');
      }
      this.#progressTweenRafId = requestAnimationFrame(step);
    };
    this.#progressTweenRafId = requestAnimationFrame(step);
  }

  #cancelProgressTween() {
    if (this.#progressTweenRafId != null) {
      cancelAnimationFrame(this.#progressTweenRafId);
      this.#progressTweenRafId = null;
    }
    this.#progressTweenStartMs = null;
  }

  /**
   * Records a real-upload percentage coming off the XHR. The tween is the
   * source of truth for what gets painted — this just raises the floor so
   * the tween can't fall behind an already-ahead network. Clamped 0–100;
   * the tween itself caps merged output at 98 until `is-complete` fires.
   * @param {number | undefined} percent
   */
  #applyUploadProgress(percent) {
    if (typeof percent !== 'number' || Number.isNaN(percent)) return;
    const clamped = Math.max(0, Math.min(100, percent));
    if (clamped > this.#progressRealMax) {
      this.#progressRealMax = clamped;
    }
  }

  /**
   * Toggles the loading state on the pet-flow "Add to Cart" button.
   * Rev 2026-04-21: replaced the gradient-sweep progress bar with a clean
   * spinner + success checkmark. The button text, spinner, and optional
   * success ✓ all animate in/out via CSS classes (`is-loading`,
   * `is-complete`). On failure, the button is restored immediately so the
   * error pill shown below is the only loud UI element.
   *
   * @param {boolean} loading
   * @param {{ succeeded?: boolean }} [options]
   *   succeeded=true plays a brief ✓ "Added!" state for ~420ms before
   *   unwinding, so the user sees confirmation right as the drawer opens.
   */
  #setNextButtonLoading(loading, options = {}) {
    const button = this.#nextButton;
    if (!button) return;
    const textSpan = button.querySelector('.pet-flow__next-button-text');
    if (!(textSpan instanceof HTMLElement)) return;

    if (loading) {
      if (!button.dataset.originalLabel) {
        button.dataset.originalLabel = textSpan.textContent || '';
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.remove('is-complete');
      button.classList.add('is-loading');
      textSpan.textContent = 'Adding to cart';
      // Keep progress tween running for XHR-upload tier (hidden gradient
      // removed, but progress var is still updated so we could restore it
      // later if we want a thin progress strip). No visual impact right now.
      this.#startProgressTween();
      return;
    }

    // Not loading — either error or success.
    const finishCleanup = () => {
      button.classList.remove('is-loading');
      button.classList.remove('is-complete');
      button.style.removeProperty('--progress');
      button.removeAttribute('aria-busy');
      if (button.dataset.originalLabel) {
        textSpan.textContent = button.dataset.originalLabel;
        delete button.dataset.originalLabel;
      }
      this.#updateNextButtonState();
    };

    this.#cancelProgressTween();

    if (options.succeeded && button.classList.contains('is-loading')) {
      // Brief "Added!" confirmation beat. Cart drawer is already opening in
      // parallel (CartAddEvent also triggers drawer.open()), so this state
      // overlays the drawer transition and disappears before it finishes.
      button.classList.remove('is-loading');
      button.classList.add('is-complete');
      textSpan.textContent = 'Added!';
      setTimeout(finishCleanup, 420);
      return;
    }

    finishCleanup();
  }

  /**
   * Render a visible error pill under the pet-flow controls. Auto-hides
   * after 6s so the UI doesn't get stuck on a stale message. Scrolls the
   * error into view — Shopify rejections (sold-out, etc.) often happen at
   * the bottom of the flow where the user may have scrolled past the
   * controls, and a silent-looking retry is the #1 cause of bounce.
   *
   * @param {string} message
   */
  #showPetFlowError(message) {
    const el = this.#errorEl;
    const textEl = this.#errorTextEl;
    if (!el || !textEl) {
      // Fallback: if the error element was not rendered by the theme
      // template, at least log to console so dev can see what happened.
      console.error('[pet-flow ATC error]', message);
      return;
    }
    textEl.textContent = message;
    el.hidden = false;
    // Ensure the message is actually in view.
    try {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (_) {
      // older browsers
    }
    if (this.#errorHideTimeoutId != null) {
      clearTimeout(this.#errorHideTimeoutId);
    }
    this.#errorHideTimeoutId = /** @type {unknown} */ (setTimeout(() => {
      el.hidden = true;
      this.#errorHideTimeoutId = null;
    }, 6000));
  }

  // ──────────────────────────────────────────────
  // Variant update (morph re-sync)
  // ──────────────────────────────────────────────

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const previousMax = this.#currentMaxIndex;
    const oldOptionCount = this.#optionGroups?.length ?? 0;
    this.#collectOptionGroups();
    if (!this.#optionGroups || !this.#optionGroups.length) return;

    // Fix morph radio-button desync: morph() updates the checked HTML attribute
    // but not the DOM .checked property. For each option group where no radio
    // has .checked === true, sync the property from the last [checked] attribute
    // (the browser treats the last attribute in DOM order as the default).
    this.#optionGroups.forEach((group) => {
      if (group.querySelector('input[type="radio"]:checked')) return; // already fine
      const withAttr = group.querySelectorAll('input[type="radio"][checked]');
      if (withAttr.length > 0) {
        // Clear stale checked attributes, keep only the last one
        withAttr.forEach((radio, i) => {
          if (i < withAttr.length - 1) radio.removeAttribute('checked');
        });
        /** @type {HTMLInputElement} */ (withAttr[withAttr.length - 1]).checked = true;
      }
    });

    const newOptionCount = this.#optionGroups.length;
    const wasOnPetDetailsStep = previousMax === oldOptionCount;

    if (wasOnPetDetailsStep) {
      this.#currentMaxIndex = newOptionCount;
    } else {
      // Keep the user on the same step — don't auto-advance just because
      // later option groups happen to have a checked radio from server HTML.
      // Only advance if the user was on step -1 (pet selector).
      if (previousMax < 0) {
        const selectedGroupIndex = this.#optionGroups.findIndex((group) =>
          group.querySelector('input:checked, select option[selected]')
        );
        if (selectedGroupIndex !== -1) {
          this.#currentMaxIndex = selectedGroupIndex;
        }
      } else {
        // Stay on the same step the user was on before the morph
        this.#currentMaxIndex = Math.min(previousMax, newOptionCount - 1);
      }
      if (previousMax >= newOptionCount) {
        this.#currentMaxIndex = newOptionCount - 1;
      }
    }

    if (this.#currentMaxIndex >= 0) {
      this.#updateOptionVisibility();
    } else if (this.#hasPetSelection()) {
      this.#currentMaxIndex = 0;
      this.#updateOptionVisibility();
    } else {
      this.#hideAllOptionGroups();
    }

    this.#syncUiFromState();

    // Update mobile preview with the variant's image from the event data.
    // We extract the URL directly from the event instead of reading the
    // gallery, because the gallery's own update may not have landed yet.
    const variantImgSrc = event?.detail?.resource?.featured_media?.preview_image?.src;
    if (variantImgSrc) {
      this.#updateMobilePreviewImage(variantImgSrc);
    } else {
      // Fallback: read from gallery after a short delay
      requestAnimationFrame(() => this.#updateMobilePreviewImage());
    }

    // Re-wire the Next-button reference after morph, and re-attach the
    // sticky-CTA observer. Otherwise the observer is still watching the
    // old button node (now detached) and silently stops firing → sticky
    // bar mysteriously stops appearing after certain option changes.
    const freshNextBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-pet-flow-next]')
    );
    if (freshNextBtn && freshNextBtn !== this.#nextButton) {
      // Transfer click listener to the fresh button.
      this.#nextButton?.removeEventListener('click', this.#handleNextClick);
      this.#nextButton = freshNextBtn;
      this.#nextButton.addEventListener('click', this.#handleNextClick);
    }
    this.#attachStickyObserver();
  };

  // ──────────────────────────────────────────────
  // Public API (called by ProductFormComponent)
  // ──────────────────────────────────────────────

  /**
   * Public entry point so ProductFormComponent can update the mobile
   * preview during local pet-count switches (before the VariantUpdateEvent).
   * @param {string} imageUrl
   */
  updateMobilePreview(imageUrl) {
    this.#updateMobilePreviewImage(imageUrl);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

if (!customElements.get('pet-variant-flow')) {
  customElements.define('pet-variant-flow', PetVariantFlow);
}
